import { TrendingUp, Activity, Users, Briefcase, Info } from "lucide-react";

function dotColor(level) {
  if (level === "good") return "bg-emerald-500";
  if (level === "ok") return "bg-amber-500";
  return "bg-rose-500";
}

function velocityLevel(v) {
  if (!v?.available) return "unknown";
  const c = v.consistency || 0;
  const t = v.total_sold || 0;
  if (c >= 0.6 && t >= 30 && (v.trend_pct ?? 0) >= -5) return "good";
  if (c >= 0.4 && t >= 15) return "ok";
  return "weak";
}

function popLevel(p) {
  if (!p?.available) return "unknown";
  const cagr = p.cagr_3yr_pct || 0;
  const cons = p.consistency || 0;
  if (cagr >= 1 && cons >= 0.66) return "good";
  if (cagr >= 0) return "ok";
  return "weak";
}

function empLevel(e) {
  if (!e?.available) return "unknown";
  const yoy = e.yoy_employment_pct ?? 0;
  const u = e.latest_unemp_rate ?? 99;
  if (yoy >= 1 && u <= 4) return "good";
  if (yoy >= 0 && u <= 5) return "ok";
  return "weak";
}

export default function MarketHealthPanel({ velocity, pop, emp, bonus }) {
  const vLevel = velocityLevel(velocity);
  const pLevel = popLevel(pop);
  const eLevel = empLevel(emp);

  return (
    <div className="surface-card mb-10" data-testid="market-health-panel">
      <div className="border-b border-[--line] px-5 py-3 flex items-center justify-between">
        <div className="overline flex items-center gap-1.5">
          <Activity size={11} strokeWidth={1.5} />
          Market health
        </div>
        {bonus ? (
          <div className="text-xs font-mono text-[--accent]">
            +{bonus} bonus added to opportunity score
          </div>
        ) : (
          <div className="text-xs font-mono text-[--ink-muted]">
            health signals · scored separately
          </div>
        )}
      </div>
      <div className="grid md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-[--line]">
        <div className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-[--ink-muted] mb-1">
                Sales velocity · last 180d
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${dotColor(vLevel)}`} />
                <span className="text-sm font-semibold capitalize">{vLevel}</span>
              </div>
            </div>
            <TrendingUp size={14} strokeWidth={1.5} className="text-[--ink-muted]" />
          </div>
          {velocity?.available ? (
            <>
              <div className="grid grid-cols-3 gap-2 text-xs mb-3 font-mono">
                <div>
                  <div className="text-[--ink-muted] text-[10px] uppercase tracking-wider">Sold</div>
                  <div className="font-semibold text-base">{velocity.total_sold}</div>
                </div>
                <div>
                  <div className="text-[--ink-muted] text-[10px] uppercase tracking-wider">Avg/mo</div>
                  <div className="font-semibold text-base">{velocity.avg_monthly}</div>
                </div>
                <div>
                  <div className="text-[--ink-muted] text-[10px] uppercase tracking-wider">Trend</div>
                  <div
                    className={`font-semibold text-base ${
                      (velocity.trend_pct ?? 0) >= 0 ? "text-emerald-700" : "text-rose-700"
                    }`}
                  >
                    {(velocity.trend_pct ?? 0) >= 0 ? "+" : ""}
                    {velocity.trend_pct}%
                  </div>
                </div>
              </div>
              <Spark counts={velocity.monthly?.map((m) => m.count) || []} />
              <div className="text-[10px] font-mono text-[--ink-muted] mt-2">
                consistency {Math.round((velocity.consistency || 0) * 100)}% ·{" "}
                {velocity.avg_dom != null ? `${velocity.avg_dom}d avg DOM` : "DOM unavailable"}
              </div>
            </>
          ) : (
            <UnavailableHint reason={velocity?.reason} />
          )}
        </div>

        <div className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-[--ink-muted] mb-1">
                Population growth · 5yr
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${dotColor(pLevel)}`} />
                <span className="text-sm font-semibold capitalize">{pLevel}</span>
              </div>
            </div>
            <Users size={14} strokeWidth={1.5} className="text-[--ink-muted]" />
          </div>
          {pop?.available ? (
            <>
              <div className="grid grid-cols-3 gap-2 text-xs mb-3 font-mono">
                <div>
                  <div className="text-[--ink-muted] text-[10px] uppercase tracking-wider">Pop</div>
                  <div className="font-semibold text-base">
                    {(pop.latest_population || 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-[--ink-muted] text-[10px] uppercase tracking-wider">3yr CAGR</div>
                  <div
                    className={`font-semibold text-base ${
                      pop.cagr_3yr_pct >= 0 ? "text-emerald-700" : "text-rose-700"
                    }`}
                  >
                    {pop.cagr_3yr_pct >= 0 ? "+" : ""}
                    {pop.cagr_3yr_pct}%
                  </div>
                </div>
                <div>
                  <div className="text-[--ink-muted] text-[10px] uppercase tracking-wider">Consistent</div>
                  <div className="font-semibold text-base">
                    {Math.round((pop.consistency || 0) * 100)}%
                  </div>
                </div>
              </div>
              <Spark counts={pop.yoy?.map((y) => y.growth_pct) || []} signed />
              <div className="text-[10px] font-mono text-[--ink-muted] mt-2">
                {pop.county} County · latest from Census {pop.latest_year}
              </div>
            </>
          ) : (
            <UnavailableHint reason={pop?.reason} />
          )}
        </div>

        <div className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-[--ink-muted] mb-1">
                Jobs · last 24mo
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${dotColor(eLevel)}`} />
                <span className="text-sm font-semibold capitalize">{eLevel}</span>
              </div>
            </div>
            <Briefcase size={14} strokeWidth={1.5} className="text-[--ink-muted]" />
          </div>
          {emp?.available ? (
            <>
              <div className="grid grid-cols-3 gap-2 text-xs mb-3 font-mono">
                <div>
                  <div className="text-[--ink-muted] text-[10px] uppercase tracking-wider">Jobs</div>
                  <div className="font-semibold text-base">
                    {(emp.latest_employment || 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-[--ink-muted] text-[10px] uppercase tracking-wider">YoY</div>
                  <div
                    className={`font-semibold text-base ${
                      (emp.yoy_employment_pct ?? 0) >= 0 ? "text-emerald-700" : "text-rose-700"
                    }`}
                  >
                    {(emp.yoy_employment_pct ?? 0) >= 0 ? "+" : ""}
                    {emp.yoy_employment_pct ?? "—"}%
                  </div>
                </div>
                <div>
                  <div className="text-[--ink-muted] text-[10px] uppercase tracking-wider">Unemp</div>
                  <div
                    className={`font-semibold text-base ${
                      (emp.latest_unemp_rate ?? 99) <= 4
                        ? "text-emerald-700"
                        : (emp.latest_unemp_rate ?? 99) <= 5.5
                        ? "text-amber-700"
                        : "text-rose-700"
                    }`}
                  >
                    {emp.latest_unemp_rate ?? "—"}%
                  </div>
                </div>
              </div>
              <Spark counts={(emp.history || []).map((h) => h.employment)} />
              <div className="text-[10px] font-mono text-[--ink-muted] mt-2">
                Latest: {emp.latest_period} · consistency{" "}
                {Math.round((emp.consistency || 0) * 100)}%
              </div>
            </>
          ) : (
            <UnavailableHint reason={emp?.reason} />
          )}
        </div>
      </div>
    </div>
  );
}

function UnavailableHint({ reason }) {
  const messages = {
    county_required: "Enter a county in the search to see this metric.",
    no_census_key: "Set CENSUS_API_KEY to enable (free signup at https://api.census.gov/data/key_signup.html).",
    no_rentcast_key: "RentCast key not configured.",
    county_not_found: "Couldn't match this county name. Try a different spelling.",
    census_unavailable: "Census API didn't respond. Try again later.",
    bls_unreachable: "BLS API didn't respond. Try again later.",
    no_data: "BLS has no data for this county yet.",
    unknown_state: "State code not recognized.",
  };
  return (
    <div className="text-xs text-[--ink-muted] flex items-start gap-1.5">
      <Info size={11} strokeWidth={1.5} className="mt-0.5 shrink-0" />
      <span>{messages[reason] || "Data unavailable for this market."}</span>
    </div>
  );
}

function Spark({ counts, signed = false }) {
  if (!counts || counts.length === 0) return <div className="h-6" />;
  const max = Math.max(...counts.map(Math.abs), 1);
  return (
    <div className="flex items-end gap-1 h-8">
      {counts.map((c, i) => {
        const h = Math.max(2, Math.abs(c) / max * 100);
        const isNeg = signed && c < 0;
        return (
          <div
            key={i}
            className={`flex-1 ${isNeg ? "bg-rose-200" : "bg-[--accent]"} opacity-60 hover:opacity-100 transition-opacity`}
            style={{ height: `${h}%` }}
            title={signed ? `${c}%` : `${c}`}
          />
        );
      })}
    </div>
  );
}
